// Ensure React is imported properly with named imports to avoid aliasing inconsistencies
import { useEffect, useState, useCallback, useMemo, forwardRef } from 'react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Users, Settings, Lock, Sparkle, ChevronDown } from 'lucide-react';
import { useProfileStore } from '@/stores/useProfileStore';
import { useMedicationStore } from '@/store/medicationStore';
import { useNavigate } from 'react-router-dom';
import { PROFILE_NAMES, PROFILE_INITIALS } from '@/constants/profileConstants';
import { UserProfile } from '@/types/UserProfile';
import { toast } from 'sonner';

/**
 * Profile switcher component for the header
 * Allows users to switch between profiles (User 1 and User 2)
 * Profile preferences management is restricted to Plus users
 * 
 * IMPORTANT: This component has been completely rewritten to ensure consistent hook ordering
 * across all render paths to avoid the React hooks ordering error.
 * 
 * Now implements forwardRef to properly handle refs from parent components
 */
interface ProfileSwitcherProps {
  onProfileChanged?: () => void;
  compact?: boolean;
}

export const ProfileSwitcher = forwardRef<HTMLButtonElement, ProfileSwitcherProps>(({
  onProfileChanged,
  compact = false
}, ref) => {
  // ====================================================
  // ALL hooks are defined here at the top level to ensure consistent ordering
  // They must be called in exactly the same order on every render
  // ====================================================
  
  // Core React hooks
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [storeError, setStoreError] = useState<Error | null>(null);
  
  // Store hooks - ALWAYS in this exact order
  const profileStore = useProfileStore();
  const medicationStore = useMedicationStore();
  


  // Derived state with useMemo - ALWAYS in this exact order
  // Use empty fallbacks to avoid errors in early render cycles
  const profiles = useMemo(() => profileStore?.profiles || [], [profileStore?.profiles]);
  
  /**
   * Always use the profile store as the single source of truth
   * This ensures consistent behavior across the application
   */
  const currentProfile = useMemo(() => {
    // Always use the value from the profile store - single source of truth
    return profileStore?.currentProfile || null;
  }, [profileStore?.currentProfile]);
  const hasPlus = useMemo(() => currentProfile?.plusTier === true, [currentProfile]);
  
  // Action callbacks - ALWAYS in this exact order
  const handlePriorityAccess = useCallback(() => {
    // First close the dropdown menu to avoid UI state issues
    setIsOpen(false);
    
    // Navigate to pricing page
    navigate('/pricing');
    
    // Use a longer timeout to ensure the page has fully rendered
    setTimeout(() => {
      try {
        // Find the email section
        const emailSection = document.querySelector('.card-footer-email-section');
        if (emailSection) {
          // Use scrollIntoView with block:center to position it in the middle of the viewport
          // and behavior:auto for immediate scrolling without animation
          emailSection.scrollIntoView({ behavior: 'auto', block: 'center' });
          
          // Find and focus on the email input with a small additional delay
          setTimeout(() => {
            const emailInput = emailSection.querySelector('input[type="email"]');
            if (emailInput instanceof HTMLInputElement && !emailInput.disabled) {
              emailInput.focus();
            }
          }, 50);
        } else {
          // Fallback behavior - just scroll to the pricing section if email section not found
          const pricingSection = document.querySelector('#pricing-section');
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        }
      } catch (error) {
        
      }
    }, 300); // Increased timeout for more reliable execution
  }, [navigate, setIsOpen]);
  
  /**
   * Handle profile change when a user selects a different profile
   * Uses the profile store as the single source of truth
   */
  const handleProfileChange = useCallback(async (id: string) => {
    try {
      // Don't switch if it's already the current profile
      if (currentProfile && id === currentProfile.id) {
        setIsOpen(false);
        return;
      }
      
      if (!profileStore || typeof profileStore.setCurrentProfile !== 'function') {
        throw new Error('Profile store not properly initialized');
      }
      
      // First check if profile exists in the store
      const targetProfile = profileStore.profiles.find(p => p.id === id);
      
      // If profile doesn't exist, create it first
      if (!targetProfile) {
        // Create a default profile for this ID
        await profileStore.createProfile({
          id: id,
          name: id === 'u1' ? 'User 1' : 'User 2',
          plusTier: false,
          sharingEnabled: false,
          preferences: {
            theme: 'system',
            notifications: {
              medicationReminders: true,
              reminderLeadTime: 30,
              refillReminders: true,
              refillReminderDays: 7
            },
            accessibility: {
              fontSize: 1,
              highContrast: false,
              reduceMotion: false
            },
            calendar: {
              defaultView: 'day',
              firstDayOfWeek: 0,
              timeFormat: '12h'
            }
          },
          type: 'primary', // Both profiles must be primary type to avoid errors
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      // Get ready to switch profiles
      
      // Only use profile store to manage the switching
      // This ensures consistent behavior and avoids race conditions
      await profileStore.setCurrentProfile(id);
      
      // Force a refresh of the medication store
      // Use type-safe approach to avoid TypeScript errors
      if (medicationStore) {
        // Refresh medications via the store, if available
        const store = medicationStore as any;
        if (typeof store.refreshMedications === 'function') {
          store.refreshMedications();
        } else {
          // Fallback - reload medications from scratch
          store.loadMedications?.();
        }
      }
      
      // Applying multiple storage updates to ensure consistency across the application
      localStorage.setItem('currentProfileId', id);
      localStorage.setItem('meduler_profile_id', id);
      
      // Set cookies for cross-storage consistency
      const date = new Date();
      date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
      document.cookie = `meduler_profile_id=${id}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
      document.cookie = `currentProfileId=${id}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
      
      // Notify the user of successful profile change
      toast.success(`Switched to ${id === 'u1' ? 'User 1' : 'User 2'} profile`);
      setIsOpen(false);
      
      // Log after the switch to verify consistency
      // Removed complex console statement for security;
      
      // Force a component refresh by triggering a brief loading state
      setIsOpen(false);
      setTimeout(() => {
        // Force component to re-render by setting state
        setIsOpen(isOpen => isOpen); // No-op state update forces re-render
        
        // Call the onProfileChanged callback if provided
        if (typeof onProfileChanged === 'function') {
          
          onProfileChanged();
        }
      }, 50);
      
    } catch (error) {
      // Removed complex console statement for security;
      toast.error('Failed to switch profiles');
    }
  }, [currentProfile, profileStore, medicationStore, onProfileChanged]);
  
  // Effects - ALWAYS last in the hooks order
  useEffect(() => {
    // Skip if profile store or initialize method is not available
    if (!profileStore || typeof profileStore.initialize !== 'function') {
      return;
    }
    
    const controller = new AbortController();
    
    const initializeProfiles = async () => {
      try {
        await profileStore.initialize(controller.signal);
      } catch (error) {
        // Removed complex console statement for security;
        setStoreError(error instanceof Error ? error : new Error('Profile initialization failed'));
      }
    };
    
    initializeProfiles();
    
    return () => controller.abort('Component unmounted');
  }, [profileStore]);
  
  // ====================================================
  // Helper functions - NOT hooks, safe to define anywhere
  // ====================================================
  
  // Get initials for avatar display
  const getInitials = (name: string) => {
    if (name === 'Primary User' || name === PROFILE_NAMES.USER_1) return PROFILE_INITIALS.USER_1;
    if (name === 'Secondary User' || name === PROFILE_NAMES.USER_2) return PROFILE_INITIALS.USER_2;
    
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Early return for loading state
  // This conditional render is fine since it's AFTER all hook calls
  if (!currentProfile) {
    return (
      <Button 
        variant="ghost" 
        size="sm"
        className="h-8 w-8 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 group transition-all duration-300 hover:border-primary/30 hover:shadow-md hover:scale-105 overflow-hidden relative"
        onClick={() => navigate('/settings/profiles')}
      >
        {/* Background animation on hover */}
        <span className="absolute inset-0 bg-primary/5 dark:bg-primary/10 transform scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full ease-out"></span>
        
        {/* Icon with animation */}
        <span className="relative z-10 transform group-hover:scale-110 transition-transform duration-300 group-hover:text-primary/90">
          <User size={18} />
        </span>
      </Button>
    );
  }
  
  // Main component render - only after all hooks have been called
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          ref={ref} /* Forward ref to the Button component */
          variant="ghost" 
          className="h-12 w-auto px-4 sm:min-w-[115px] sm:px-5 rounded-full flex items-center justify-center bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 transform hover:scale-110 hover:shadow-lg transition-all duration-300 hover:border-primary/30 focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
          aria-label="Switch profile"
        >
          <span className="flex items-center justify-center h-full w-full gap-1 whitespace-nowrap">
            {/* Full name on desktop, initial on mobile */}
            <span className="hidden sm:inline text-primary font-medium">
              {currentProfile?.id === 'u1' ? 'User 1' : 'User 2'}
            </span>
            <span className="sm:hidden text-primary font-medium">
              {currentProfile?.id === 'u1' ? 'U1' : 'U2'}
            </span>
          </span>
          <ChevronDown className="h-3 w-3 text-gray-500 ml-1" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-56">
        {/* Removed label, will be placed next to the dropdown in the header */}
        
        {/* Simplified profile switching between U1 and U2 */}
        <DropdownMenuItem
          key="u1"
          className={`flex items-center gap-2 ${currentProfile?.id === 'u1' ? 'bg-muted cursor-default' : 'cursor-pointer'}`}
          disabled={currentProfile?.id === 'u1'}
          onClick={() => handleProfileChange('u1')}
        >
          <Avatar className="h-6 w-6">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              U1
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm">User 1</span>
            <span className="text-xs text-muted-foreground">
              Primary User
            </span>
          </div>
          {currentProfile?.id === 'u1' && currentProfile?.plusTier && (
            <Badge className="ml-auto h-4 px-1 text-[10px] bg-amber-100 text-amber-700 border-amber-200">
              Plus
            </Badge>
          )}
        </DropdownMenuItem>
        
        <DropdownMenuItem
          key="u2"
          className={`flex items-center gap-2 ${currentProfile?.id === 'u2' ? 'bg-muted cursor-default' : 'cursor-pointer'}`}
          disabled={currentProfile?.id === 'u2'}
          onClick={() => handleProfileChange('u2')}
        >
          <Avatar className="h-6 w-6">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              U2
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm">User 2</span>
            <span className="text-xs text-muted-foreground">
              Secondary User
            </span>
          </div>
          {currentProfile?.id === 'u2' && currentProfile?.plusTier && (
            <Badge className="ml-auto h-4 px-1 text-[10px] bg-amber-100 text-amber-700 border-amber-200">
              Plus
            </Badge>
          )}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Locked profile preferences for non-Plus users */}
        {hasPlus ? (
          <>
            <DropdownMenuItem 
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/settings/profiles')}
            >
              <Users size={16} />
              <span>Manage Profiles</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/settings/preferences')}
            >
              <Settings size={16} />
              <span>Preferences</span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              className="flex items-center gap-2 text-muted-foreground"
              disabled
            >
              <Lock size={16} />
              <span>Profile Management (Plus Only)</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem
              className="flex items-center gap-2 text-muted-foreground"
              disabled
            >
              <Lock size={16} />
              <span>Preferences (Plus Only)</span>
            </DropdownMenuItem>
          </>
        )}
        
        {/* Always show upgrade option for everyone */}
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer text-amber-600"
          onClick={handlePriorityAccess}
        >
          <span>Waitlist for Plus</span>
          <Sparkle size={16} />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
